// =====================================================================
// src/screens/v2/WallPostComposer.jsx
//
// Phase 2.5 / C10b — Full-screen overlay composer for wall posts.
//
// Used in two places:
//   1. MerchantApp Wall tab's "Compose" button
//   2. MerchantApp Today tab's "New post" Quick Action
//
// Props:
//   mode          'create' | 'edit'
//   businessId    string, required
//   businessName  string, displayed in header
//   ownerUid      string, required (passed to upload helpers)
//   existing      post object when mode='edit', otherwise null
//   onClose       () => void
//   onSaved       (postId, { photosUploaded, photosFailed }) => void
//   onSetToast    (string) => void  (for error messages)
//
// Photo handling: up to 3 slots. In create mode each slot holds either
// null or a File (raw). In edit mode each slot holds null, a URL string
// (existing photo to keep), or a File (new upload to replace/add). The
// composer hands those arrays straight to wallPosts.create/update.
// =====================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, ImagePlus, Trash2 } from 'lucide-react';
import {
  POST_TYPES,
  MAX_POST_TEXT,
  MAX_POST_PHOTOS,
  createWallPost,
  updateWallPost,
} from '../../lib/v2/wallPosts';
import { validateImageFile } from '../../lib/v2/storagePhotos';

export default function WallPostComposer({
  mode = 'create',
  businessId,
  businessName,
  ownerUid,
  existing = null,
  onClose,
  onSaved,
  onSetToast,
}) {
  // ---- State init from `existing` (edit mode) or defaults (create) ----
  // photoSlots is a length-3 array where each entry is:
  //   null          — empty slot
  //   string (URL)  — existing photo we want to keep (edit mode)
  //   File          — new file to upload
  // Storing files in the same array as URLs keeps the slot order stable
  // when the user reorders / removes photos.
  const initialSlots = useMemo(() => {
    if (mode === 'edit' && existing?.photos) {
      const slots = [null, null, null];
      existing.photos.slice(0, MAX_POST_PHOTOS).forEach((url, i) => { slots[i] = url; });
      return slots;
    }
    return [null, null, null];
  }, [mode, existing]);

  const [type, setType] = useState(existing?.type || 'general');
  const [text, setText] = useState(existing?.text || '');
  const [photoSlots, setPhotoSlots] = useState(initialSlots);
  const [submitting, setSubmitting] = useState(false);

  // Capture the original photos array as it was when the composer opened
  // so updateWallPost can diff against the current state. Stable across
  // edits during the composer session.
  const originalPhotosRef = useRef(initialSlots);

  // ---- Body scroll lock while modal is open ----
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ---- Photo handlers ----
  function handlePickPhoto(slotIndex, e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file again triggers onChange
    if (!file) return;
    try {
      validateImageFile(file);
    } catch (err) {
      onSetToast(err.message || 'Image not supported');
      return;
    }
    setPhotoSlots(prev => {
      const next = [...prev];
      next[slotIndex] = file;
      return next;
    });
  }

  function handleRemovePhoto(slotIndex) {
    setPhotoSlots(prev => {
      const next = [...prev];
      next[slotIndex] = null;
      return next;
    });
  }

  // Render preview URL for a slot — either the existing URL, or a
  // blob URL for a freshly-picked File. Blob URLs revoked on unmount.
  const blobUrlsRef = useRef([]);
  const previewUrls = useMemo(() => {
    // Revoke previously-created blob URLs before creating new ones to
    // avoid leaking object URLs across renders.
    blobUrlsRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
    blobUrlsRef.current = [];
    return photoSlots.map(slot => {
      if (!slot) return null;
      if (typeof slot === 'string') return slot; // existing URL
      // File — create blob URL
      const u = URL.createObjectURL(slot);
      blobUrlsRef.current.push(u);
      return u;
    });
  }, [photoSlots]);

  useEffect(() => {
    // Cleanup on unmount: revoke any remaining blob URLs.
    return () => {
      blobUrlsRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
    };
  }, []);

  // ---- Validation ----
  const photoCount = photoSlots.filter(s => s).length;
  const trimmed = text.trim();
  const hasContent = trimmed.length > 0 || photoCount > 0;
  const overLimit = text.length > MAX_POST_TEXT;
  const canSubmit = hasContent && !overLimit && !submitting;
  const charsLeft = MAX_POST_TEXT - text.length;

  // ---- Submit ----
  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (mode === 'create') {
        // For create, we only need to send the File objects (in slot order).
        const photoFiles = photoSlots.filter(s => s && typeof s !== 'string');
        const result = await createWallPost({
          bizId: businessId,
          authorId: ownerUid,
          type,
          text: trimmed,
          photoFiles,
        });
        if (result.photosFailed > 0) {
          onSetToast(`Posted, but ${result.photosFailed} photo${result.photosFailed > 1 ? 's' : ''} failed to upload`);
        } else {
          onSetToast('Post published');
        }
        onSaved?.(result.postId, result);
        onClose?.();
      } else {
        // Edit. Diff currentPhotos vs nextPhotos for updateWallPost.
        const currentPhotos = originalPhotosRef.current; // strings or null
        const nextPhotos = photoSlots.map(s => (typeof s === 'string' ? s : null));
        const photoFiles = photoSlots.map(s => (s instanceof File ? s : null));

        await updateWallPost({
          bizId: businessId,
          postId: existing.id,
          ownerUid,
          updates: {
            type,
            text: trimmed,
            currentPhotos,
            nextPhotos,
            photoFiles,
          },
        });
        onSetToast('Post updated');
        onSaved?.(existing.id, {});
        onClose?.();
      }
    } catch (err) {
      console.error('WallPostComposer submit failed:', err);
      onSetToast(err?.message || 'Could not save post');
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Render ----
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg)',
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 shrink-0"
           style={{ borderBottom: '1px solid var(--border)' }}>
        <button onClick={onClose}
                className="w-9 h-9 rounded-full flex items-center justify-center active:scale-95"
                aria-label="Close composer">
          <X size={20} style={{ color: 'var(--ink)' }} />
        </button>
        <div className="text-[15px] font-medium" style={{ color: 'var(--ink)' }}>
          {mode === 'edit' ? 'Edit post' : 'New post'}
        </div>
        <button onClick={handleSubmit}
                disabled={!canSubmit}
                className="px-4 py-2 rounded-full text-[13px] font-medium transition-opacity"
                style={{
                  background: canSubmit ? 'var(--ink)' : 'var(--border)',
                  color: canSubmit ? '#fff' : 'var(--ink-soft)',
                  opacity: submitting ? 0.6 : 1,
                }}>
          {submitting ? 'Saving…' : (mode === 'edit' ? 'Save' : 'Post')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {businessName && (
          <div className="text-[12px] mb-3" style={{ color: 'var(--ink-soft)' }}>
            Posting as <strong style={{ color: 'var(--ink)' }}>{businessName}</strong>
          </div>
        )}

        {/* Type chips */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>
            Post type
          </div>
          <div className="flex flex-wrap gap-2">
            {POST_TYPES.map(t => {
              const active = type === t.id;
              return (
                <button key={t.id}
                        onClick={() => setType(t.id)}
                        className="px-3 py-2 rounded-full text-[13px] font-medium flex items-center gap-1.5 transition-colors"
                        style={{
                          background: active ? 'var(--ink)' : 'var(--surface)',
                          color: active ? '#fff' : 'var(--ink)',
                          border: '1px solid ' + (active ? 'var(--ink)' : 'var(--border)'),
                        }}>
                  <span>{t.emoji}</span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Text */}
        <div className="mb-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              type === 'offer'      ? 'Describe the offer…' :
              type === 'new_product' ? 'Introduce the new product…' :
              type === 'announcement' ? 'What do you want to announce?' :
              'Share an update with your customers…'
            }
            rows={6}
            className="w-full p-3 rounded-xl text-[14px] leading-snug resize-none"
            style={{
              background: 'var(--surface)',
              border: '1px solid ' + (overLimit ? '#C45A3F' : 'var(--border)'),
              color: 'var(--ink)',
              outline: 'none',
            }}
          />
          <div className="flex justify-end mt-1">
            <span className="text-[11px]"
                  style={{ color: overLimit ? '#C45A3F' : 'var(--ink-soft)' }}>
              {charsLeft} characters left
            </span>
          </div>
        </div>

        {/* Photo strip */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>
            Photos · {photoCount} / {MAX_POST_PHOTOS}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map(slotIndex => {
              const slot = photoSlots[slotIndex];
              const preview = previewUrls[slotIndex];
              if (slot) {
                return (
                  <div key={slotIndex}
                       className="aspect-square rounded-xl overflow-hidden relative"
                       style={{ border: '1px solid var(--border)' }}>
                    <img src={preview} alt=""
                         style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button onClick={() => handleRemovePhoto(slotIndex)}
                            className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full flex items-center justify-center active:scale-95"
                            style={{ background: 'rgba(0,0,0,0.55)' }}
                            aria-label="Remove photo">
                      <Trash2 size={14} color="#fff" />
                    </button>
                  </div>
                );
              }
              return (
                <label key={slotIndex}
                       className="aspect-square rounded-xl flex flex-col items-center justify-center cursor-pointer active:scale-[0.98] transition-transform"
                       style={{
                         background: 'var(--surface)',
                         border: '1px dashed var(--border)',
                         color: 'var(--ink-soft)',
                       }}>
                  <ImagePlus size={20} />
                  <span className="text-[10px] mt-1">Add</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                         onChange={(e) => handlePickPhoto(slotIndex, e)} />
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
